<template>
  <div>
    <!-- Toolbar lives in the parent shell. This view renders only the
         analytics body, driven by the `analytics` Vuex module's
         applied filters. -->
    <div>
      <!-- Stat cards -->
      <div class="kpi-grid mb-6">
        <template v-if="panelLoading.kpis">
          <div v-for="i in 6" :key="'skel-stat-' + i" class="bg-white rounded-xl border border-gray-200/60 p-5">
            <LoadingBlock height="80" label="Loading stats..." />
          </div>
        </template>
        <template v-else-if="data && data.sessions">
          <div
            v-for="(stat, i) in statCards"
            :key="stat.label"
            :class="[
              'animate-fade-in relative',
              `stagger-${i + 1}`,
              activeInfoCard === (stat.key || stat.label) ? 'z-50' : ''
            ]"
          >
            <div class="bg-white rounded-xl border border-gray-200/60 p-5">
              <div class="flex items-center justify-between mb-3">
                <div class="flex items-center gap-1.5">
                  <span class="text-sm font-medium text-gray-400 uppercase tracking-wider">{{ stat.label }}</span>
                  <div v-if="stat.info" class="relative">
                    <button
                      @click.stop="toggleInfo(stat.key || stat.label)"
                      type="button"
                      class="w-4 h-4 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-700 flex items-center justify-center text-[10px] font-semibold leading-none transition-colors"
                      :title="stat.info"
                    >?</button>
                    <!-- Info popover — anchored to the `?` button.
                         Last card right-anchors so it grows leftward
                         and doesn't push horizontal scroll. -->
                    <div
                      v-if="activeInfoCard === (stat.key || stat.label)"
                      @click.stop
                      :class="[
                        'absolute top-full mt-2 z-50 w-64 bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm text-gray-600 leading-relaxed',
                        i === statCards.length - 1 ? 'right-0' : 'left-0'
                      ]"
                    >{{ stat.info }}</div>
                  </div>
                </div>
                <span :class="['flex items-center justify-center w-9 h-9 rounded-lg', stat.iconBg]" v-html="stat.icon" />
              </div>

              <div class="flex items-baseline gap-2">
                <div class="font-data text-[26px] font-medium text-gray-900 leading-none">{{ stat.value }}</div>
                <DeltaBadge v-if="stat.current != null" :current="stat.current" :previous="stat.previous" :inverse="!!stat.inverse" :formatter="stat.format || formatNumber" />
              </div>
              <p class="text-sm text-gray-400 mt-2">{{ stat.sub }}</p>
            </div>
          </div>
        </template>
      </div>

      <!-- Sessions — single-series line chart. Previously carried a
           second "Streams" (5s-validated) series; retired since the
           new session model already scopes sessions to actual plays. -->
      <div class="bg-white rounded-xl border border-gray-200/60 mb-6">
        <div class="px-6 py-3 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h2 class="text-[15px] font-semibold text-gray-900">Sessions</h2>
            <p class="text-sm text-gray-400 mt-0.5">Sessions per time bucket</p>
          </div>
          <span v-if="hasData('viewers.concurrent')" class="text-sm font-data font-medium text-[var(--color-primary)] bg-[var(--color-primary-light)] px-3 py-1 rounded">
            {{ formatNumber(chartTotal(data.viewers.concurrent, 'count')) }} sessions
          </span>
        </div>
        <div class="p-6">
          <LoadingBlock v-if="panelLoading.sessionsTimeline" height="350" label="Loading chart..." />
          <template v-else-if="hasData('viewers.concurrent')">
            <div class="h-[350px] relative animate-fade-in"><canvas ref="concurrentViewersChart"></canvas></div>
          </template>
          <template v-else><EmptyChart label="No session data for this period" /></template>
        </div>
      </div>

      <!-- Unique Users Chart -->
      <div class="bg-white rounded-xl border border-gray-200/60 mb-6">
        <div class="px-6 py-3 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h2 class="text-[15px] font-semibold text-gray-900">Unique Users</h2>
            <p class="text-sm text-gray-400 mt-0.5">Distinct viewers per time bucket</p>
          </div>
          <span v-if="data && data.uniqueUsers" class="text-sm font-data font-medium text-[var(--color-primary)] bg-[var(--color-primary-light)] px-3 py-1 rounded">
            {{ formatNumber(data.uniqueUsers.total) }} total
          </span>
        </div>
        <div class="p-6">
          <LoadingBlock v-if="panelLoading.uniqueUsers" height="350" label="Loading chart..." />
          <template v-else-if="hasData('uniqueUsers.timeline')">
            <div class="h-[350px] relative animate-fade-in"><canvas ref="uniqueUsersChart"></canvas></div>
          </template>
          <template v-else><EmptyChart label="No unique users data for this period" /></template>
        </div>
      </div>

      <!-- Top Content + Geographic Distribution — same component
           pair the Overview tab uses. TopContentCard auto-hides
           empty tabs, so on a per-live view (only Shows + Categories
           have data) it renders just those two pills. Geo card mixes
           two panels — the rollup-derived top-N (fast) and the
           session-derived lat/lng markers (slow) — so its loading
           state stays true while EITHER is still fetching. -->
      <div class="mb-6">
        <TopContentCard
          :loading="panelLoading.topContent"
          :data="data?.topContent || {}"
          :tabs="['lives', 'shows', 'categories']"
        />
      </div>
      <div class="mb-6">
        <GeoDistributionCard
          :loading="panelLoading.geoDistribution || panelLoading.geographyMap"
          :data="data?.topGeo || {}"
          :geography="data?.geography || []"
        />
      </div>

      <!-- Demographics — each on its own card -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div class="bg-white rounded-xl border border-gray-200/60">
          <div class="px-6 py-3 border-b border-gray-100 flex items-center justify-between">
            <div>
              <h2 class="text-[15px] font-semibold text-gray-900">Age Range</h2>
              <p class="text-sm text-gray-400 mt-0.5">Audience age distribution</p>
            </div>
            <span v-if="hasData('demographics.ageRange')" class="text-sm font-data font-medium text-[var(--color-primary)] bg-[var(--color-primary-light)] px-3 py-1 rounded">
              {{ formatNumber(chartTotal(data.demographics.ageRange)) }} total
            </span>
          </div>
          <div class="p-6">
            <LoadingBlock v-if="panelLoading.demographics" height="260" label="Loading chart..." />
            <template v-else-if="hasData('demographics.ageRange')">
              <div class="h-[260px] relative animate-fade-in"><canvas ref="ageRangeChart"></canvas></div>
            </template>
            <template v-else><EmptyChart label="No age data available" /></template>
          </div>
        </div>

        <div class="bg-white rounded-xl border border-gray-200/60">
          <div class="px-6 py-3 border-b border-gray-100 flex items-center justify-between">
            <div>
              <h2 class="text-[15px] font-semibold text-gray-900">Gender</h2>
              <p class="text-sm text-gray-400 mt-0.5">Audience gender breakdown</p>
            </div>
            <span v-if="hasData('demographics.gender')" class="text-sm font-data font-medium text-[var(--color-primary)] bg-[var(--color-primary-light)] px-3 py-1 rounded">
              {{ formatNumber(chartTotal(data.demographics.gender)) }} total
            </span>
          </div>
          <div class="p-6">
            <LoadingBlock v-if="panelLoading.demographics" height="260" label="Loading chart..." />
            <template v-else-if="hasData('demographics.gender')">
              <div class="h-[260px] relative animate-fade-in"><canvas ref="genderChart"></canvas></div>
            </template>
            <template v-else><EmptyChart label="No gender data available" /></template>
          </div>
        </div>

        <div class="bg-white rounded-xl border border-gray-200/60">
          <div class="px-6 py-3 border-b border-gray-100 flex items-center justify-between">
            <div>
              <h2 class="text-[15px] font-semibold text-gray-900">Language</h2>
              <p class="text-sm text-gray-400 mt-0.5">Preferred viewer languages</p>
            </div>
            <span v-if="hasData('demographics.language')" class="text-sm font-data font-medium text-[var(--color-primary)] bg-[var(--color-primary-light)] px-3 py-1 rounded">
              {{ formatNumber(chartTotal(data.demographics.language)) }} total
            </span>
          </div>
          <div class="p-6">
            <LoadingBlock v-if="panelLoading.demographics" height="260" label="Loading chart..." />
            <template v-else-if="hasData('demographics.language')">
              <div class="h-[260px] relative animate-fade-in"><canvas ref="languageChart"></canvas></div>
            </template>
            <template v-else><EmptyChart label="No language data available" /></template>
          </div>
        </div>
      </div>

      <!-- Operating Systems + Devices + Browsers -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div class="bg-white rounded-xl border border-gray-200/60">
          <div class="px-6 py-3 border-b border-gray-100 flex items-center justify-between">
            <div>
              <h2 class="text-[15px] font-semibold text-gray-900">Operating Systems</h2>
              <p class="text-sm text-gray-400 mt-0.5">Viewer OS distribution</p>
            </div>
            <span v-if="hasData('operatingSystems')" class="text-sm font-data font-medium text-[var(--color-primary)] bg-[var(--color-primary-light)] px-3 py-1 rounded">
              {{ formatNumber(chartTotal(data.operatingSystems)) }} total
            </span>
          </div>
          <div class="p-6">
            <LoadingBlock v-if="panelLoading.devices" height="260" label="Loading chart..." />
            <template v-else-if="hasData('operatingSystems')">
              <div class="h-[260px] relative animate-fade-in"><canvas ref="osChart"></canvas></div>
            </template>
            <template v-else><EmptyChart label="No OS data available" /></template>
          </div>
        </div>

        <div class="bg-white rounded-xl border border-gray-200/60">
          <div class="px-6 py-3 border-b border-gray-100 flex items-center justify-between">
            <div>
              <h2 class="text-[15px] font-semibold text-gray-900">Devices</h2>
              <p class="text-sm text-gray-400 mt-0.5">Viewer device types</p>
            </div>
            <span v-if="hasData('devices')" class="text-sm font-data font-medium text-[var(--color-primary)] bg-[var(--color-primary-light)] px-3 py-1 rounded">
              {{ formatNumber(chartTotal(data.devices)) }} total
            </span>
          </div>
          <div class="p-6">
            <LoadingBlock v-if="panelLoading.devices" height="260" label="Loading chart..." />
            <template v-else-if="hasData('devices')">
              <div class="h-[260px] relative animate-fade-in"><canvas ref="deviceChart"></canvas></div>
            </template>
            <template v-else><EmptyChart label="No device data available" /></template>
          </div>
        </div>

        <div class="bg-white rounded-xl border border-gray-200/60">
          <div class="px-6 py-3 border-b border-gray-100 flex items-center justify-between">
            <div>
              <h2 class="text-[15px] font-semibold text-gray-900">Browsers</h2>
              <p class="text-sm text-gray-400 mt-0.5">Viewer browser distribution</p>
            </div>
            <span v-if="hasData('browsers')" class="text-sm font-data font-medium text-[var(--color-primary)] bg-[var(--color-primary-light)] px-3 py-1 rounded">
              {{ formatNumber(chartTotal(data.browsers)) }} total
            </span>
          </div>
          <div class="p-6">
            <LoadingBlock v-if="panelLoading.devices" height="260" label="Loading chart..." />
            <template v-else-if="hasData('browsers')">
              <div class="h-[260px] relative animate-fade-in"><canvas ref="browserChart"></canvas></div>
            </template>
            <template v-else><EmptyChart label="No browser data available" /></template>
          </div>
        </div>
      </div>

      <!-- Top Referrers + Top Embed Hosts — same BreakdownCard
           pattern the Overview view uses. Referrers show where the
           listener came from; Embed Hosts show where the player is
           running. -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
        <BreakdownCard title="Top Referrers"   subtitle="Where listeners came from" :loading="panelLoading.referrers" :rows="topRows('referrers')"  empty-label="No referrer data" />
        <BreakdownCard title="Top Embed Hosts" subtitle="Where the player is running" :loading="panelLoading.referrers" :rows="topRows('embedHosts')" empty-label="No embed host data" />
      </div>

      <!-- Event Breakdown, Quality Metrics, and Errors charts have been
           temporarily removed pending a perf pass. See removed-charts.md
           for the original template + JS blocks and reactivation
           instructions. -->
    </div>
  </div>
</template>

<script>
import Chart from 'chart.js/auto'
import { mapGetters } from 'vuex'
import LoadingBlock from '../../components/LoadingBlock.vue'
import DeltaBadge from './components/DeltaBadge.vue'
import BreakdownCard from './components/BreakdownCard.vue'
import TopContentCard from './components/TopContentCard.vue'
import GeoDistributionCard from './components/GeoDistributionCard.vue'
import api from '../../services/api.js'
import { notifyError } from '../../services/notifications.js'
import { buildTabReport } from './report-export.js'

// Consistent color palette for all charts
const COLORS = {
  primary: '#3393ff',
  primaryFill: 'rgba(51, 147, 255, 0.08)',
  bar: 'rgba(51, 147, 255, 0.65)',
  doughnut: ['#3393ff', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#6b7280'],
  grid: '#f5f5f5',
  tick: '#a3a3a3'
}

const CHART_SCALES = {
  x: { grid: { display: false }, ticks: { font: { family: 'Poppins', size: 11 }, color: COLORS.tick } },
  y: { grid: { color: COLORS.grid }, ticks: { font: { family: 'JetBrains Mono', size: 11 }, color: COLORS.tick } }
}

// Empty state component
const EmptyChart = {
  props: { label: String },
  template: `
    <div class="flex flex-col items-center justify-center py-12 text-center">
      <div class="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center mb-3">
        <svg class="w-5 h-5 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
          <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>
        </svg>
      </div>
      <p class="text-base text-gray-400">{{ label }}</p>
    </div>
  `
}

export default {
  name: 'AnalyticsLives',
  components: { EmptyChart, LoadingBlock, DeltaBadge, BreakdownCard, TopContentCard, GeoDistributionCard },

  data() {
    return {
      // Aggregate `data` — each panel merges its slice in as its
      // request completes, so per-chart `hasData()` checks work
      // unchanged. Starts as `{}` (not null) so v-if reads on nested
      // paths don't NPE before the first panel lands.
      data: {},
      charts: {},
      // Per-panel loading flags. Each chart card checks its own key
      // (`v-if="panelLoading.kpis"` etc.) so a slow panel (unique-users,
      // geography-map, event-breakdown) doesn't block fast panels from
      // painting. Keys mirror the API URL segment: `/analytics/{key}/lives`.
      panelLoading: {
        kpis: false, sessionsTimeline: false, uniqueUsers: false,
        topContent: false, geoDistribution: false, geographyMap: false,
        demographics: false, devices: false, referrers: false
      },
      // Currently-open KPI info popover. Holds the card key (or
      // label fallback) of the card whose `?` button was last
      // clicked. Document-level click handler closes it on
      // outside click — installed in mounted, removed in
      // beforeUnmount.
      activeInfoCard: null
    }
  },

  computed: {
    ...mapGetters('analytics', ['applied']),

    /**
     * Aggregate loading flag — true whenever ANY panel request is in
     * flight. Kept as a computed rather than a mutable state so the
     * parent toolbar's `@loading` emission stays a single reactive
     * source and per-panel flag changes don't cause drift.
     */
    loading() {
      return Object.values(this.panelLoading).some(Boolean)
    },

    /**
     * Six KPI cards driven by the new session model:
     *   1. Unique Users
     *   2. Sessions
     *   3. Total Session Duration
     *   4. Avg Session Duration
     *   5. Total Watch Time          (content-only ms; ads + paused excluded)
     *   6. Avg Watch Time
     *
     * Playback Starts / Streams (validated 5s+) were retired — the
     * new session_started fires on the first play intent, so a
     * session existing IS a "playback start."
     */
    statCards() {
      if (!this.data) return []
      const s = this.data.sessions || {}
      const u = this.data.uniqueUsers || {}
      return [
        {
          key: 'uniqueUsers',
          label: 'Unique Users',
          value: this.formatNumber(u.total),
          current: u.total || 0,
          previous: u.totalPrev,
          sub: 'Distinct viewers in period',
          info: 'Distinct listeners in the period. One person opening the player three times counts as 1.',
          icon: '<svg class="w-4 h-4 text-rose-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>',
          iconBg: 'bg-rose-50'
        },
        {
          key: 'sessions',
          label: 'Sessions',
          value: this.formatNumber(s.total || 0),
          current: s.total || 0,
          previous: s.totalPrev,
          sub: 'Sessions in period',
          info: 'Count of sessions in the window. A session opens on the first play intent and closes on SDK destroy or a 30-minute continuous-pause timeout. A fresh play after an idle-timeout mints a new session.',
          icon: '<svg class="w-4 h-4 text-blue-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="18" x2="12" y2="21"/></svg>',
          iconBg: 'bg-blue-50'
        },
        {
          key: 'totalSessionDuration',
          label: 'Total Session Duration',
          value: this.formatDuration(s.totalDurationMs),
          current: s.totalDurationMs || 0,
          previous: s.totalDurationMsPrev,
          format: this.formatDuration,
          sub: 'Wall-clock across sessions',
          info: 'Sum of session_duration across every session. Wall-clock = playing (content + ads) + paused. Includes idle-timeout sessions.',
          icon: '<svg class="w-4 h-4 text-indigo-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>',
          iconBg: 'bg-indigo-50'
        },
        {
          key: 'avgSessionDuration',
          label: 'Avg Session Duration',
          value: this.formatDuration(s.avgDurationMs),
          current: s.avgDurationMs || 0,
          previous: s.avgDurationMsPrev,
          format: this.formatDuration,
          sub: 'Total duration ÷ sessions',
          info: 'Average wall-clock time a session lasted. Formula: total_session_duration ÷ sessions.',
          icon: '<svg class="w-4 h-4 text-green-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>',
          iconBg: 'bg-green-50'
        },
        {
          key: 'totalWatchTime',
          label: 'Total Watch Time',
          value: this.formatDuration(s.totalWatchTimeMs),
          current: s.totalWatchTimeMs || 0,
          previous: s.totalWatchTimeMsPrev,
          format: this.formatDuration,
          sub: 'Content-only playing time',
          info: 'Sum of time the media element was playing content across every session. Excludes ads (tracked separately in the Ads section) and paused / buffering periods. This is "how long people listened to your content."',
          icon: '<svg class="w-4 h-4 text-violet-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>',
          iconBg: 'bg-violet-50'
        },
        {
          key: 'avgWatchTime',
          label: 'Avg Watch Time',
          value: this.formatDuration(s.avgWatchTimeMs),
          current: s.avgWatchTimeMs || 0,
          previous: s.avgWatchTimeMsPrev,
          format: this.formatDuration,
          sub: 'Content play per session',
          info: 'Average content-play time per session. Formula: total_watch_time ÷ sessions.',
          icon: '<svg class="w-4 h-4 text-amber-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>',
          iconBg: 'bg-amber-50'
        }
      ]
    }
  },

  mounted() {
    // Deep-link support: /analytics/lives?id=X seeds the store so the
    // picked live carries through into the toolbar + this view's first
    // fetch without needing the user to click Search.
    if (this.$route.query.id && !this.applied.liveId) {
      this.$store.dispatch('analytics/seed', { liveId: String(this.$route.query.id) })
    }
    // Non-reactive instance state:
    //   _runId:      monotonic counter — bumped every loadAnalytics() call.
    //                Every in-flight fetch captures the value from when it
    //                was queued; if it doesn't match by the time the
    //                response lands, the fetch drops silently (a newer
    //                filter set has taken over).
    //   _panelChain: promise chain that serializes all panel fetches.
    //   _abort:      AbortController for every in-flight /analytics/*
    //                request. Bumped on loadAnalytics() and on unmount so
    //                a tab-nav or filter-change abandons the previous
    //                run's outstanding HTTP requests instead of letting
    //                them complete and get discarded via the runId guard.
    this._runId = 0
    this._panelChain = Promise.resolve()
    this._abort = new AbortController()

    this.$nextTick(() => {
      this.loadAnalytics()
      this._installLazyPanelObserver()
    })
    // Close info popover on outside click. Bound to document so
    // any click anywhere outside a card's `?`/popover dismisses it
    // (the popover itself stops propagation, the `?` toggles).
    this._onDocClick = () => { this.activeInfoCard = null }
    document.addEventListener('click', this._onDocClick)
  },

  watch: {
    // Reload whenever the toolbar's APPLY commits new filter values.
    // Watching the entire `applied` object catches date / live / show
    // changes alike. Deep watch because nested fields mutate.
    applied: { handler() { this.loadAnalytics() }, deep: true }
  },

  beforeUnmount() {
    // Abort the controller but DON'T null it — subsequent queued
    // `_fetchPanel` calls still fire off the promise chain after
    // unmount; they need `signal` to remain truthy-and-aborted so the
    // outgoing `api.get` rejects immediately instead of firing an
    // unstoppable request. `_fetchPanel` also short-circuits on the
    // aborted signal so no network work happens either way.
    if (this._abort) this._abort.abort()
    Object.values(this.charts).forEach(c => { if (c) c.destroy() })
    if (this._onDocClick) document.removeEventListener('click', this._onDocClick)
    if (this._panelObserver) { this._panelObserver.disconnect(); this._panelObserver = null }
  },

  methods: {
    /**
     * Report for the Download button (PDF / CSV) — built from the data
     * this tab already loaded, so it matches the screen. Null while
     * nothing has loaded yet.
     */
    buildReport() {
      if (!this.data) return null
      const a = this.applied
      return buildTabReport({
        title: 'Lives Audience Report',
        kpiCards: this.statCards,
        data: this.data,
        applied: a,
        granularity: this.granularityFor(
          a.dateFrom ? new Date(a.dateFrom).toISOString() : null,
          a.dateTo ? new Date(a.dateTo).toISOString() : null,
          a.presetLabel
        )
      })
    },

    /**
     * Toggle the per-card info popover. Same key click-twice closes;
     * different key swaps to the new card; null closes all.
     */
    toggleInfo(key) {
      this.activeInfoCard = this.activeInfoCard === key ? null : key
    },

    hasData(path) {
      const parts = path.split('.')
      let obj = this.data
      for (const p of parts) {
        obj = obj?.[p]
        if (obj == null) return false
      }
      return Array.isArray(obj) ? obj.length > 0 : !!obj
    },

    /**
     * Load analytics by fanning out one request per chart panel.
     *
     * Every panel URL follows `/analytics/{panelKey}/{scope}` where
     * scope mirrors what the original monolithic endpoint took
     * (`lives` aggregate or `lives/:id` for a single live). Each panel
     * updates its own slice of `this.data`, flips its own loading
     * flag, and renders its own charts as soon as its data arrives —
     * so fast panels (rollup-derived) paint at ~250 ms while slow
     * ones (unique-users, geography-map, event-breakdown) fill in
     * later without blocking anything.
     *
     * The parent toolbar's `@loading` emission is driven by the
     * aggregate `loading` computed, which is true whenever ANY panel
     * is still in flight.
     */
    async loadAnalytics() {
      const a = this.applied
      const params = {}
      if (a.dateFrom) params.dateFrom = new Date(a.dateFrom).toISOString()
      if (a.dateTo)   params.dateTo   = new Date(a.dateTo).toISOString()
      if (a.showId)  params.showId  = a.showId
      if (a.appName) params.appName = a.appName
      if (a.categoryIds?.length) params.categoryId = a.categoryIds.join(',')
      params.granularity = this.granularityFor(params.dateFrom, params.dateTo, a.presetLabel)

      // Per-stream (lives/:id) vs cross-stream (lives) scope. Same
      // switch the old monolithic loader used, now applied to every
      // panel URL.
      const scope = a.liveId ? `lives/${a.liveId}` : 'lives'

      // Reset accumulated data + per-chart canvases so a re-fetch
      // (date change, live selection change) starts clean.
      this.data = {}
      Object.values(this.charts).forEach(c => { if (c) c.destroy() })
      this.charts = {}

      // ---- TEMP: isolation testing ----
      // Restrict the eager fetches to a specific subset while we verify
      // per-panel behaviour after the writer rewrite. Add panel keys to
      // the array to enable them; set to null to load every eager panel.
      const ISOLATE_PANELS = null

      // The panel table — one row per API URL. `render` runs after the
      // panel's data has merged into `this.data`; return null when a
      // panel has no chart to draw (KPI strip, breakdown lists that
      // render directly from the reactive data). `lazy: true` panels
      // aren't fired here — they wait for the IntersectionObserver
      // in mounted() to hit them when the card scrolls near the
      // viewport (see PANEL_TABLE + `_panelObserver`).
      const panels = this.panelTable(scope, params)
        .filter(p => !p.lazy)
        .filter(p => !ISOLATE_PANELS || ISOLATE_PANELS.includes(p.key))

      // Loading flags — only the panels we're actually going to fetch
      // get flipped to `true`. Everything else stays `false` so the
      // corresponding cards render their empty / no-data state
      // instead of a spinner while we run the isolation test.
      const activeKeys = new Set(panels.map(p => p.key))
      for (const key of Object.keys(this.panelLoading)) {
        this.panelLoading[key] = activeKeys.has(key)
      }
      this.$emit('loading', true)

      // Save the applied params so the lazy-panel observer can fire
      // the deferred panels with the SAME params later. Cleared and
      // reset on every loadAnalytics call so a re-fetch with new
      // filters propagates. Re-register observer targets so panels
      // already viewed (fired) refetch under the new filters.
      this._lastPanelParams = { scope, params }
      if (this._firedLazyPanels) this._firedLazyPanels.clear()
      if (this._panelObserver) {
        for (const el of document.querySelectorAll('[data-lazy-panel]')) {
          this._panelObserver.observe(el)
        }
      }

      // Serialize the panel fetches — one at a time, in declared
      // panelTable() order. Every fetch (eager AND lazy) flows through
      // `_enqueue()` which appends onto a shared promise chain, so
      // even if a lazy panel is observed mid-flight it waits its
      // turn behind whatever's currently running rather than
      // stampeding the DB. This keeps the connection pool small and
      // gives each query full DB capacity for its duration.
      //
      // Abort any in-flight requests from the previous run before
      // starting the new one. Without this, filter-change / re-render
      // would leave the old requests to complete in the background
      // (only their results being dropped via the runId guard). The
      // new controller is what every _fetchPanel below will attach
      // its signal to.
      if (this._abort) this._abort.abort()
      this._abort = new AbortController()

      // Bump the run id so any in-flight fetches from a superseded
      // loadAnalytics() (previous filter set) know to drop their
      // result silently and not merge stale data into the fresh view.
      const runId = ++this._runId
      // Reset the queue chain — an in-flight fetch will still finish,
      // but nothing NEW from the old queue will run. New enqueues
      // start a fresh chain gated on the current promise.
      this._panelChain = Promise.resolve()
      for (const p of panels) this._enqueue(() => this._fetchPanel(p, runId))
      await this._panelChain
      if (this._runId === runId) this.$emit('loading', false)
    },

    /**
     * Append a task to the serial panel-fetch chain and return the
     * chain's new tail. Errors don't break the chain — each task's
     * error is swallowed here so a failed panel doesn't stop
     * subsequent ones from running.
     */
    _enqueue(task) {
      this._panelChain = (this._panelChain || Promise.resolve())
        .then(() => task().catch(() => {}))
      return this._panelChain
    },

    /**
     * Table of every panel the view knows how to fetch. Callable so
     * the render closures can capture the correct `this`. `lazy: true`
     * panels defer until the card scrolls near the viewport.
     */
    panelTable(scope, params) {
      // `geographyMap` is queued last on purpose — it runs a sampled
      // session scan (~3s) that other panels shouldn't wait behind.
      // The map pane inside GeoDistributionCard flips on as soon as
      // `data.geography` populates.
      return [
        { key: 'kpis',             url: 'kpis',             scope, params },
        { key: 'sessionsTimeline', url: 'sessions-timeline', scope, params, render: () => this.renderConcurrentViewersChart() },
        { key: 'uniqueUsers',      url: 'unique-users',     scope, params, render: () => this.renderUniqueUsersChart() },
        { key: 'topContent',       url: 'top-content',      scope, params },
        { key: 'geoDistribution',  url: 'geo-distribution', scope, params },
        { key: 'demographics',     url: 'demographics',     scope, params, render: () => {
          this.renderAgeRangeChart()
          this.renderGenderChart()
          this.renderLanguageChart()
        } },
        { key: 'devices',          url: 'devices',          scope, params, render: () => {
          this.renderOsChart()
          this.renderDeviceChart()
          this.renderBrowserChart()
        } },
        { key: 'referrers',        url: 'referrers',        scope, params },
        { key: 'geographyMap',     url: 'geography-map',    scope, params }
      ]
    },

    /**
     * Install an IntersectionObserver over every element carrying a
     * `data-lazy-panel="<key>"` attribute. When the card scrolls
     * within 400px of the viewport (rootMargin), the panel's request
     * fires. Once fetched, the observer stops watching that node so
     * subsequent scroll ticks are free.
     *
     * The observer persists across `loadAnalytics()` re-runs — the
     * lazy cards are still in the DOM after filter changes, only
     * their data is stale. `_firedLazyPanels` is reset per fetch so
     * a new filter set forces re-observing.
     */
    _installLazyPanelObserver() {
      if (typeof IntersectionObserver === 'undefined') {
        // Old browsers or SSR — just fire lazy panels eagerly.
        this._panelObserver = null
        this._fireAllLazy()
        return
      }
      this._firedLazyPanels = new Set()
      this._panelObserver = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue
          const key = entry.target.dataset.lazyPanel
          if (!key || this._firedLazyPanels.has(key)) continue
          this._firedLazyPanels.add(key)
          this._panelObserver.unobserve(entry.target)
          this._fireLazyPanel(key)
        }
      }, { rootMargin: '400px 0px' })
      for (const el of document.querySelectorAll('[data-lazy-panel]')) {
        this._panelObserver.observe(el)
      }
    },

    /** Look up the deferred panel by key and enqueue it behind whatever's
     *  currently fetching. Same serial chain as the eager panels — if
     *  the user scrolls to a lazy card while eager panels are still
     *  loading, the lazy fetch waits its turn. */
    _fireLazyPanel(key) {
      if (!this._lastPanelParams) return
      const { scope, params } = this._lastPanelParams
      const p = this.panelTable(scope, params).find(x => x.key === key)
      if (!p) return
      const runId = this._runId
      this._enqueue(() => this._fetchPanel(p, runId))
    },

    /** Fallback: enqueue all lazy panels (no IntersectionObserver). */
    _fireAllLazy() {
      if (!this._lastPanelParams) return
      const { scope, params } = this._lastPanelParams
      const lazy = this.panelTable(scope, params).filter(p => p.lazy)
      const runId = this._runId
      for (const p of lazy) this._enqueue(() => this._fetchPanel(p, runId))
    },

    /**
     * Fetch one panel, merge its slice into `this.data`, and run its
     * render callback (if any). Shared by the eager and lazy paths.
     *
     * `runId` guards against stale merges: if `loadAnalytics()` is
     * called again while this fetch is awaiting the API, the run id
     * increments and we drop the response silently rather than
     * merging outdated data into the freshly-reset view.
     */
    async _fetchPanel(p, runId) {
      if (runId != null && this._runId !== runId) return
      const signal = this._abort ? this._abort.signal : undefined
      // Short-circuit if the controller has already been aborted (view
      // unmounted, or a newer loadAnalytics run superseded us). Without
      // this, the queued panels behind an in-flight request would still
      // call `api.get` — each rejects fast on the aborted signal, but
      // we save the round-trip entirely by returning here.
      if (signal && signal.aborted) return
      try {
        const res = await api.get(`/analytics/${p.url}/${p.scope}`, { params: p.params, silent: true, signal })
        if (runId != null && this._runId !== runId) return
        // One-level deep merge: per-namespace fields accumulate across
        // panel responses instead of wiping each other out. Two panels
        // legitimately share the `viewers` namespace (sessions-timeline
        // owns concurrent/timeline/streamsTimeline; unique-users adds
        // granularity), and a shallow `...` would drop the earlier
        // response's fields when the later one lands.
        const incoming = res.data || {}
        const merged = { ...this.data }
        for (const [k, v] of Object.entries(incoming)) {
          merged[k] = (v && typeof v === 'object' && !Array.isArray(v) && merged[k] && typeof merged[k] === 'object' && !Array.isArray(merged[k]))
            ? { ...merged[k], ...v }
            : v
        }
        this.data = merged
      } catch (err) {
        if (runId != null && this._runId !== runId) return
        // AbortController.abort() surfaces as either a CanceledError
        // (axios) or an AbortError (fetch/other). Both are expected
        // during nav / filter-change; skip the toast so users don't
        // see red errors for cancellation the app itself triggered.
        if (err?.name === 'CanceledError' || err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') return
        notifyError(err?.message || `Failed to load ${p.url}`)
      } finally {
        if (runId == null || this._runId === runId) {
          this.panelLoading[p.key] = false
          if (p.render) {
            await this.$nextTick()
            p.render()
          }
        }
      }
    },

    destroyChart(key) {
      if (this.charts[key]) { this.charts[key].destroy(); this.charts[key] = null }
    },

    /**
     * Resolve the bucket granularity from the API response. New API
     * surfaces `data.viewers.granularity` ('minute'|'hour'|'day'|'month'|'year');
     * older payloads only have `isHourly`. Default to 'day' if neither
     * is set so charts still render against unknown payloads.
     */
    bucketGranularity() {
      const g = this.data?.viewers?.granularity
      if (g) return g
      return this.data?.viewers?.isHourly === false ? 'day' : 'hour'
    },

    /**
     * Decide the chart bucket size for a given date range. Presets map
     * to fixed buckets (Last Hour=minute; Last 6h/24h/Yesterday=hour;
     * Last 7d/30d/This Month/Last Month=day; Last Year=month). 'Custom'
     * (or anything not in the table) falls through to a duration-based
     * mapping: <1h minute, <24h hour, <31d day, <365d month, ≥365d year.
     * Sent to the API as `?granularity=...` so the server picks the
     * matching rollup tier; the same value drives x-axis label
     * formatting locally.
     */
    granularityFor(dateFrom, dateTo, presetLabel) {
      const presetMap = {
        'Last Hour':     'minute',
        'Last 6 Hours':  'hour',
        'Today':         'hour',
        'Last 24 Hours': 'hour',
        'Yesterday':     'hour',
        'Last 7 Days':   'day',
        'Last 30 Days':  'day',
        'This Month':    'day',
        'Last Month':    'day',
        'Last Year':     'month'
      }
      if (presetMap[presetLabel]) return presetMap[presetLabel]
      // Custom or unknown: derive from duration.
      if (!dateFrom || !dateTo) return 'hour'
      const ms = new Date(dateTo) - new Date(dateFrom)
      const hours = ms / 3600000
      const days  = hours / 24
      if (hours < 1)   return 'minute'
      if (hours < 24)  return 'hour'
      if (days  < 31)  return 'day'
      if (days  < 365) return 'month'
      return 'year'
    },

    /**
     * Render a bucket key as a human label given the active granularity.
     *   minute → "14:23"
     *   hour   → "2 PM"
     *   day    → "May 1"
     *   month  → "May 2026"
     *   year   → "2026"
     */
    formatChartLabel(raw, granularity = 'hour', localDate = null) {
      if (!raw) return raw
      if (granularity === 'minute') {
        // localDate path used by gap-fill (already a Date in local TZ).
        if (localDate) {
          const hh = String(localDate.getHours()).padStart(2, '0')
          const mm = String(localDate.getMinutes()).padStart(2, '0')
          return `${hh}:${mm}`
        }
        // Raw key path: '2026-05-01 14:23' → '14:23'
        const m = raw.match(/(\d{2}):(\d{2})$/)
        return m ? `${m[1]}:${m[2]}` : raw
      }
      if (granularity === 'hour') {
        if (localDate) {
          const h = localDate.getHours()
          const ampm = h >= 12 ? 'PM' : 'AM'
          const h12 = h % 12 || 12
          return `${h12} ${ampm}`
        }
        const match = raw.match(/(\d{2}):00$/)
        if (match) {
          const h = parseInt(match[1])
          const ampm = h >= 12 ? 'PM' : 'AM'
          const h12 = h % 12 || 12
          return `${h12} ${ampm}`
        }
        return raw
      }
      if (granularity === 'day') {
        // "2026-05-01" → "May 1"
        const d = new Date(raw + 'T12:00:00')
        if (isNaN(d)) return raw
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      }
      if (granularity === 'month') {
        // "2026-05" → "May 2026"
        const d = new Date(raw + '-01T12:00:00')
        if (isNaN(d)) return raw
        return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
      }
      // year — "2026"
      return raw
    },

    /**
     * Roll day-grain rows (`{_id: 'YYYY-MM-DD', count: N}`) up to a
     * coarser bucket ('month' / 'year'). Returns rows unchanged for
     * 'day' or any finer granularity — callers force those to 'day'
     * before calling since finer-than-day rollup isn't meaningful for
     * daily-materialized data.
     */
    dailyToGranularity(rows, target) {
      if (target !== 'month' && target !== 'year') return rows
      const bucket = {}
      for (const r of rows) {
        const iso = r._id || ''
        const key = target === 'month' ? iso.substring(0, 7) : iso.substring(0, 4)
        if (!key) continue
        bucket[key] = (bucket[key] || 0) + (r.count || 0)
      }
      return Object.entries(bucket)
        .map(([k, v]) => ({ _id: k, count: v }))
        .sort((a, b) => a._id.localeCompare(b._id))
    },

    fillTimeGaps(data, valueKey, granularity) {
      const map = {}
      data.forEach(item => { map[item._id] = item[valueKey] || 0 })

      // Walk the actual applied range from `dateFrom` → `dateTo` in
      // bucket-sized steps. The previous implementation hard-coded
      // 60-minute / 24-hour windows for the `minute` / `hour`
      // branches, which made "Last 6 Hours" render 24 hourly slots
      // and "Last Hour" render exactly one hour even when the user
      // picked a wider custom range. Now every granularity uses the
      // same range-driven walk; only the step size and bucket-key
      // format differ per tier.
      const startIso = this.applied?.dateFrom ? new Date(this.applied.dateFrom).toISOString() : (data.length && data[0]._id)
      const endIso   = this.applied?.dateTo   ? new Date(this.applied.dateTo).toISOString()   : (data.length && data[data.length - 1]._id)
      if (!startIso || !endIso) return { labels: [], values: [] }

      const start = new Date(this.parseFillStart(startIso, granularity))
      const end   = new Date(this.parseFillStart(endIso, granularity))
      const labels = []
      const values = []

      const step = (d) => {
        if (granularity === 'minute') d.setUTCMinutes(d.getUTCMinutes() + 1)
        if (granularity === 'hour')   d.setUTCHours(d.getUTCHours() + 1)
        if (granularity === 'day')    d.setUTCDate(d.getUTCDate() + 1)
        if (granularity === 'month')  d.setUTCMonth(d.getUTCMonth() + 1)
        if (granularity === 'year')   d.setUTCFullYear(d.getUTCFullYear() + 1)
      }

      // Build the rollup-row key the way the writer wrote it (UTC
      // ISO prefixed to the bucket size). `localDate` passed to the
      // formatter is the same instant in the browser's locale so
      // labels render in the user's timezone even though the key is
      // UTC.
      const keyFor = (d) => {
        const iso = d.toISOString()
        if (granularity === 'minute') return iso.substring(0, 16).replace('T', ' ')
        if (granularity === 'hour')   return iso.substring(0, 13).replace('T', ' ') + ':00'
        if (granularity === 'day')    return iso.substring(0, 10)
        if (granularity === 'month')  return iso.substring(0, 7)
        return String(d.getUTCFullYear())
      }

      for (let d = new Date(start); d <= end; step(d)) {
        const key = keyFor(d)
        // For minute/hour the formatter can show clock-time in the
        // browser's locale when given the Date instance; for day/
        // month/year the existing key-only path is sufficient.
        const usesLocal = granularity === 'minute' || granularity === 'hour'
        labels.push(this.formatChartLabel(key, granularity, usesLocal ? d : null))
        values.push(map[key] || 0)
      }
      return { labels, values }
    },

    /**
     * Coerce a filter date string into a UTC Date snapped to the start
     * of the relevant bucket. Handles every granularity tier:
     *   year   → Jan 1 00:00 UTC of the year
     *   month  → 1st 00:00 UTC of the month
     *   day    → 00:00 UTC of the day
     *   hour   → on-the-hour UTC
     *   minute → on-the-minute UTC
     */
    parseFillStart(s, granularity) {
      if (granularity === 'year')  return Date.UTC(parseInt(s.substring(0, 4)), 0, 1)
      if (granularity === 'month') return Date.UTC(parseInt(s.substring(0, 4)), parseInt(s.substring(5, 7)) - 1 || 0, 1)
      if (granularity === 'day')   return new Date(s.substring(0, 10) + 'T00:00:00Z').getTime()
      const d = new Date(s)
      if (granularity === 'hour')   d.setUTCMinutes(0, 0, 0)
      if (granularity === 'minute') d.setUTCSeconds(0, 0)
      return d.getTime()
    },

    renderUniqueUsersChart() {
      const ctx = this.$refs.uniqueUsersChart
      if (!ctx) return
      this.destroyChart('uniqueUsers')
      // Server returns rows at the requested granularity for
      // minute/hour/day (hour/minute via a session scan, day via the
      // materialized collection with a session-scan fallback). For
      // month/year it still returns day-grain rows and we roll them up
      // client-side — dailyToGranularity is a no-op for finer targets.
      const granularity = this.bucketGranularity()
      const d = this.dailyToGranularity(this.data.uniqueUsers?.timeline || [], granularity)
      const { labels, values } = this.fillTimeGaps(d, 'count', granularity)
      // Violet tint to visually distinguish from the blue concurrent /
      // bar timeline charts that follow.
      const violet = '#8b5cf6'
      const violetFill = 'rgba(139, 92, 246, 0.08)'
      this.charts.uniqueUsers = new Chart(ctx, {
        type: 'line',
        data: {
          labels,
          datasets: [{ data: values, borderColor: violet, backgroundColor: violetFill, tension: 0.4, fill: true, borderWidth: 2, pointRadius: 2, pointHoverRadius: 5 }]
        },
        options: { responsive: true, maintainAspectRatio: false, animation: false, plugins: { legend: { display: false } }, scales: { ...CHART_SCALES } }
      })
    },

    /**
     * Sessions & Streams chart — two-line funnel visualization.
     *   Line 1 (blue, filled) : sessions started per bucket
     *   Line 2 (green, line)  : validated streams per bucket
     * The vertical gap between the two reads as the bounce cohort
     * (sessions that started but didn't survive 5s of continuous
     * playback). Both series share the same x-axis labels from
     * `fillTimeGaps`, so the buckets line up exactly.
     */
    renderConcurrentViewersChart() {
      const ctx = this.$refs.concurrentViewersChart
      if (!ctx) return
      this.destroyChart('concurrent')
      const granularity = this.bucketGranularity()
      const sessionsData = this.data.viewers?.concurrent || []
      const sessions = this.fillTimeGaps(sessionsData, 'count', granularity)
      this.charts.concurrent = new Chart(ctx, {
        type: 'line',
        data: {
          labels: sessions.labels,
          datasets: [
            {
              label: 'Sessions',
              data: sessions.values,
              borderColor: COLORS.primary,
              backgroundColor: COLORS.primaryFill,
              tension: 0.4,
              fill: true,
              borderWidth: 2,
              pointRadius: 2,
              pointHoverRadius: 5
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: false,
          interaction: { mode: 'index', intersect: false },
          plugins: {
            // Single-series chart — legend adds no information; the
            // card header already says "Sessions".
            legend: { display: false }
          },
          scales: { ...CHART_SCALES }
        }
      })
    },


    renderDeviceChart() {
      const ctx = this.$refs.deviceChart
      if (!ctx) return
      this.destroyChart('device')
      const d = this.data.devices || []
      const fmt = (t) => t ? t.charAt(0).toUpperCase() + t.slice(1) : 'Unknown'
      this.charts.device = new Chart(ctx, {
        type: 'doughnut',
        data: {
          labels: d.map(x => fmt(x._id)),
          datasets: [{ data: d.map(x => x.count), backgroundColor: COLORS.doughnut.slice(0, d.length) }]
        },
        options: {
          responsive: true, maintainAspectRatio: false, animation: false, cutout: '65%',
          plugins: { legend: { position: 'bottom', labels: { font: { family: 'Poppins', size: 12 }, padding: 16, usePointStyle: true, pointStyle: 'circle' } } }
        }
      })
    },

    renderOsChart() {
      const ctx = this.$refs.osChart
      if (!ctx) return
      this.destroyChart('os')
      const d = this.data.operatingSystems || []
      const fmt = (t) => t || 'Unknown'
      this.charts.os = new Chart(ctx, {
        type: 'doughnut',
        data: {
          labels: d.map(x => fmt(x._id)),
          datasets: [{ data: d.map(x => x.count), backgroundColor: COLORS.doughnut.slice(0, d.length) }]
        },
        options: {
          responsive: true, maintainAspectRatio: false, animation: false, cutout: '65%',
          plugins: { legend: { position: 'bottom', labels: { font: { family: 'Poppins', size: 12 }, padding: 16, usePointStyle: true, pointStyle: 'circle' } } }
        }
      })
    },

    renderBrowserChart() {
      const ctx = this.$refs.browserChart
      if (!ctx) return
      this.destroyChart('browser')
      const d = this.data.browsers || []
      const fmt = (t) => t || 'Unknown'
      this.charts.browser = new Chart(ctx, {
        type: 'doughnut',
        data: {
          labels: d.map(x => fmt(x._id)),
          datasets: [{ data: d.map(x => x.count), backgroundColor: COLORS.doughnut.slice(0, d.length) }]
        },
        options: {
          responsive: true, maintainAspectRatio: false, animation: false, cutout: '65%',
          plugins: { legend: { position: 'bottom', labels: { font: { family: 'Poppins', size: 12 }, padding: 16, usePointStyle: true, pointStyle: 'circle' } } }
        }
      })
    },

    /**
     * Top Referrers / Top Embed Hosts use BreakdownCard now (same
     * pattern as the Overview view). The mapping happens in
     * `topRows()` below — no more per-card chart renderers here.
     */
    topRows(path) {
      const parts = path.split('.')
      let arr = this.data
      for (const p of parts) arr = arr?.[p]
      if (!Array.isArray(arr)) return []
      return arr.slice(0, 10).map(r => ({
        id:    r._id,
        label: r.title || r._id || 'Unknown',
        count: r.count || 0
      }))
    },

    renderAgeRangeChart () {
      const ctx = this.$refs.ageRangeChart
      if (!ctx) return
      this.destroyChart('ageRange')
      const order = ['18+', '21+', '18-34', '18-49', '21-34', '18-24', '21-24', '25-34', '25-49', '25-54', '35-44', '45-54', '55-64', '55+', '65+']
      const vals = order.map(age => {
        const f = (this.data.demographics?.ageRange || []).find(d => d._id === age)
        return f ? f.count : 0
      })
      this.charts.ageRange = new Chart(ctx, {
        type: 'bar',
        data: { labels: order, datasets: [{ data: vals, backgroundColor: COLORS.bar, borderRadius: 4 }] },
        options: {
          responsive: true,
          maintainAspectRatio: false, animation: false,
          plugins: { legend: { display: false } },
          scales: {
            ...CHART_SCALES,
            x: { ...CHART_SCALES.x, ticks: { ...CHART_SCALES.x.ticks, font: { family: 'Poppins', size: 10 } } }
          }
        }
      })
    },

    renderGenderChart() {
      const ctx = this.$refs.genderChart
      if (!ctx) return
      this.destroyChart('gender')
      const d = this.data.demographics?.gender || []
      this.charts.gender = new Chart(ctx, {
        type: 'doughnut',
        data: {
          labels: d.map(x => x._id),
          datasets: [{ data: d.map(x => x.count), backgroundColor: COLORS.doughnut.slice(0, d.length) }]
        },
        options: {
          responsive: true, maintainAspectRatio: false, animation: false, cutout: '65%',
          plugins: { legend: { position: 'bottom', labels: { font: { family: 'Poppins', size: 12 }, padding: 16, usePointStyle: true, pointStyle: 'circle' } } }
        }
      })
    },

    renderLanguageChart() {
      const ctx = this.$refs.languageChart
      if (!ctx) return
      this.destroyChart('language')
      // Map keyed in lowercase ISO codes; values come in uppercase
      // post-demographics-normalization, so lowercase the key on
      // lookup to handle both cases without duplicating entries.
      const langNames = { en: 'English', es: 'Spanish', fr: 'French', de: 'German', pt: 'Portuguese', it: 'Italian', ja: 'Japanese', zh: 'Chinese', ko: 'Korean', ar: 'Arabic', hi: 'Hindi', ru: 'Russian' }
      const d = this.data.demographics?.language || []
      this.charts.language = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: d.map(x => langNames[String(x._id || '').toLowerCase()] || x._id),
          datasets: [{ data: d.map(x => x.count), backgroundColor: COLORS.bar, borderRadius: 4 }]
        },
        options: {
          indexAxis: 'y', responsive: true, maintainAspectRatio: false, animation: false, plugins: { legend: { display: false } },
          scales: {
            x: { grid: { color: COLORS.grid }, ticks: { font: { family: 'JetBrains Mono', size: 10 }, color: COLORS.tick } },
            y: { grid: { display: false }, ticks: { font: { family: 'Poppins', size: 11 }, color: '#737373' } }
          }
        }
      })
    },

    formatNumber(n) {
      if (n == null || isNaN(n)) return '—'
      if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M'
      if (n >= 1_000)     return (n / 1_000).toFixed(1) + 'K'
      return Number(n).toLocaleString()
    },

    /**
     * Sum a numeric field across an array. Used for the "X total" badges
     * in each chart header. Defaults to summing `count`; pass another key
     * for arrays whose value field differs (e.g. timeline uses `sessions`).
     */
    chartTotal(arr, key = 'count') {
      if (!Array.isArray(arr)) return 0
      return arr.reduce((s, x) => s + (Number(x?.[key]) || 0), 0)
    },

    formatDuration(ms) {
      if (!ms) return '0s'
      const s = Math.floor(ms / 1000), m = Math.floor(s / 60), h = Math.floor(m / 60)
      if (h > 0) return `${h}h ${m % 60}m`
      if (m > 0) return `${m}m ${s % 60}s`
      return `${s}s`
    }
  }
}
</script>

